import React, { useEffect } from "react";
import qs from "querystring";
import {
  login,
  logout,
  CLIENT_ID,
  REDIRECT_URI,
  COGNITO_OAUTH_ENDPOINT,
} from "../../utils/auth/AuthService";
import SessionManager from "../../utils/auth/SessionManager";

const Auth = () => {
  useEffect(() => {
    const authenticate = async () => {
      let responseUrlCode = document.location.search;
      const urlParams = new URLSearchParams(responseUrlCode);
      const authCode = urlParams.get('code');
      const returnedState = urlParams.get('state');

      if (!authCode) {
        console.error("No authorization code found in URL");
        // Redirect to dashboard, which will trigger re-authentication
        window.location.href = "/dashboard";
        return;
      }

      // Validate state parameter to prevent CSRF/session fixation
      const storedState = sessionStorage.getItem('oauth_state');
      if (!returnedState || returnedState !== storedState) {
        console.error('OAuth state validation failed - possible CSRF attack');
        console.error('Returned state:', returnedState);
        console.error('Stored state:', storedState);
        sessionStorage.removeItem('oauth_state');
        window.location.href = "/dashboard";
        return;
      }
      sessionStorage.removeItem('oauth_state');  // Clear after successful validation

      const tokensReqBody = {
        grant_type: "authorization_code",
        code: authCode,
        client_id: CLIENT_ID,
        redirect_uri: REDIRECT_URI,
      };

      const headers = { "Content-Type": "application/x-www-form-urlencoded" };

      try {
        console.log("Exchanging auth code for tokens...");
        console.log("Request body:", tokensReqBody);
        
        const response = await fetch(`${COGNITO_OAUTH_ENDPOINT}/token`, {
          method: "POST",
          headers,
          body: qs.stringify(tokensReqBody),
        });

        if (response.ok) {
          const tokenPayload = await response.json();
          console.log("Token exchange successful");
          
          const expires_in = tokenPayload.expires_in
            ? parseInt(tokenPayload.expires_in) / 60
            : 60;

          const expireDate = new Date();
          expireDate.setMinutes(expireDate.getMinutes() + expires_in);

          const id_token = tokenPayload.id_token;
          const access_token = tokenPayload.access_token;
          const refresh_token = tokenPayload.refresh_token;

          // Store tokens
          login(
            id_token,
            access_token,
            refresh_token,
            expireDate.toUTCString()
          );

          // Wait a bit to ensure tokens are persisted
          await new Promise(resolve => setTimeout(resolve, 100));

          const originUrl = SessionManager.getOriginUrl();
          SessionManager.removeOriginUrl();
          
          console.log("Redirecting to:", originUrl || "/dashboard");
          window.location.href = originUrl || "/dashboard";
        } else {
          const errorText = await response.text();
          console.error("Token exchange failed:", response.status, errorText);
          console.error("Request body was:", tokensReqBody);
          
          // Clear any partial auth state
          logout();
          
          // Redirect to dashboard, which will trigger re-authentication
          // Add a flag to prevent immediate loop
          sessionStorage.setItem('auth_error', 'token_exchange_failed');
          sessionStorage.setItem('auth_error_time', Date.now().toString());
          window.location.href = "/dashboard";
        }
      } catch (error) {
        console.error("Token exchange error:", error);
        logout();
        sessionStorage.setItem('auth_error', 'token_exchange_error');
        sessionStorage.setItem('auth_error_time', Date.now().toString());
        window.location.href = "/dashboard";
      }
    };
    authenticate();
  }, []);

  return null;
};

export default Auth;
